/**
 * Форма объявления о грузе — создание и правка, блок `WEB-2.1`, требования
 * 301, 302 (правка), 306, 314, 315 и 318.
 *
 * Форма отправляет **ровно** поля `CargoListingInput` и ничего сверх, а кузов —
 * ровно поля `CargoBodyInput`. Поле формы названо именем поля тела
 * (`weight_kg`, `body.length_m`): тем же именем сервер называет поле в отказе,
 * и его сообщение ложится под своим полем единственным механизмом
 * `renderFieldMessages`; сообщение о поле, которого на форме нет, — общим
 * текстом над формой.
 *
 * Чего форма **не** делает, названо здесь же, потому что соблазн каждого — в
 * одну строку. Километраж, страны транзита и число границ считает сервер и
 * отдаёт карточкой созданного объявления; предварительного числа в форме нет
 * (решение `Р4` А). Даты, пределы, обязательность полей и дней отсрочки судит
 * сервер (`422` под полем): поле «дни отсрочки» видно при любом моменте оплаты,
 * пустое уходит `null` (решение формы `Б10`). Какие параметры кузова применимы
 * к типу, говорит справочник `listBodyTypes` — своей таблицы «тип →
 * параметры» у клиента нет, и смена типа убирает поля прежнего: их значения не
 * отправляются. Торг — переключатель, в новой форме он стоит в «торг
 * разрешён» и уходит строгим логическим (требование 318).
 *
 * Создание несёт `Idempotency-Key` (решение формы `Б5`): повтор отправки до
 * ответа — тот же ключ, а повтор, пока запрос в пути, вовсе не уходит; новый
 * ключ — после ответа сервера или после правки поля. Обрыв связи ответа не
 * дал, и ключ остаётся: сервер, возможно, объявление уже завёл. Правка ключа
 * не несёт (поправка CONTRACT `К6`). Отправка только онлайн (решение `Б6`).
 *
 * Смена языка не стирает введённое (долг `TD-72`): оболочка не пересобирает
 * экран, а переподписывает его — поля и значения остаются теми же узлами.
 */

import { ApiFailure, newIdempotencyKey } from '../api/client';
import type { ApiClient, CargoBodySubmission, CargoListingSubmission } from '../api/client';
import type { BodyTypeInfo, CargoBodyInput, CargoListing, City } from '../api/contract';
import {
  BODY_TYPES,
  CURRENCIES,
  LOADING_TYPES,
  PAYMENT_METHODS,
  PAYMENT_MOMENTS,
} from '../api/generated/enums';
import {
  bodyParameterStringKey,
  bodyTypeStringKey,
  currencyStringKey,
  loadingTypeStringKey,
  paymentMethodStringKey,
  paymentMomentStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import { navigate } from '../router';
import {
  createFieldMessageSlot,
  enumLabel,
  FIELD_MESSAGE_CLASS,
  placeState,
  refusalText,
  renderFieldMessages,
  screenStateFor,
  selectEnumOption,
} from './state';
import type { ScreenState } from './state';

export interface CargoListingFormScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  /** Объявление правки; без него — форма новой записи. */
  readonly listingId?: string;
}

export interface CargoListingFormScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — форма показана. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
  /** Подписи на новом языке; введённое остаётся на месте (долг `TD-72`). */
  relabel(next: Strings): void;
}

/** Поля параметров кузова в теле — всё, кроме типа и подтипа. */
type BodyFieldName = Exclude<keyof CargoBodyInput, 'type' | 'subtype'>;

/** Форма значения поля в теле: десятичная строка, целое или тип загрузки. */
type BodyFieldKind = 'decimal' | 'integer' | 'loading';

/**
 * Форма каждого поля параметров кузова — **схемой тела**, а не правилом:
 * размеры и тонны уходят десятичной строкой (двоичная дробь теряет знаки
 * молча, как у денег), температура и вместимость — целым, тип загрузки —
 * значением порождённого перечня. Какие поля показать, говорит справочник:
 * здесь только, каким элементом поле вводится. Запись полна по построению —
 * поле, добавленное в схему, ломает типы.
 */
const BODY_FIELD_KINDS: Readonly<Record<BodyFieldName, BodyFieldKind>> = {
  length_m: 'decimal',
  width_m: 'decimal',
  height_m: 'decimal',
  volume_m3: 'decimal',
  capacity_t: 'decimal',
  loading_type: 'loading',
  temperature_min_c: 'integer',
  temperature_max_c: 'integer',
  cars_capacity: 'integer',
};

const BODY_FIELD_NAMES = Object.keys(BODY_FIELD_KINDS) as BodyFieldName[];

/**
 * Поля тела, которыми справочник называет параметр. Имя параметра — имя поля;
 * параметр-пара (температура) называет **два** поля одним именем, и они
 * начинаются с него (так объявляет `CargoBodyParameter` контракта). Это
 * сопоставление имён, а не применимость: применимые параметры выбрал сервер.
 */
function fieldsOf(parameter: string): BodyFieldName[] {
  const found: BodyFieldName[] = [];

  for (const name of BODY_FIELD_NAMES) {
    if (name === parameter || name.startsWith(`${parameter}_`)) {
      found.push(name);
    }
  }

  return found;
}

/**
 * Имя поля кузова на форме — `body.<поле>`: так сервер называет поле
 * вложенного тела в отказе, и его сообщение ляжет под этим полем.
 */
function bodyFieldName(field: string): string {
  return `body.${field}`;
}

/** Значение порождённого перечня, совпавшее с выбранным, либо `null`. */
function pick<T extends string>(values: readonly T[], chosen: string): T | null {
  for (const value of values) {
    if (value === chosen) {
      return value;
    }
  }

  return null;
}

/** Пустое поле — `null`; обязательность и пределы судит сервер. */
function textOf(raw: string): string | null {
  return raw === '' ? null : raw;
}

function decimalOf(raw: string): string | null {
  return textOf(raw.trim());
}

function integerOf(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw);
}

/** Подпись города в подсказке: имя на языке ответа и страна под ним. */
function cityCaption(city: City): string {
  return `${city.name} (${city.country_name})`;
}

export function createCargoListingFormScreen(options: CargoListingFormScreenOptions): CargoListingFormScreen {
  const { client, companyId, listingId } = options;
  let strings = options.strings;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const notice = document.createElement('p');
  const form = document.createElement('form');
  const bodyArea = document.createElement('div');
  const submitButton = document.createElement('button');

  element.className = 'screen screen-cargo-listing-form';
  notice.className = 'notice';
  notice.setAttribute('role', 'alert');
  form.className = 'form';
  form.noValidate = true;
  bodyArea.className = 'form';
  submitButton.type = 'submit';
  submitButton.className = 'action';

  /**
   * Подписи формы: каждая ставится одной записью отсюда — и при сборке, и при
   * смене языка. Поля и введённое в них этим не трогаются (долг `TD-72`).
   */
  const captions: Array<() => void> = [];

  function caption(apply: () => void): void {
    captions.push(apply);
    apply();
  }

  function labelled(control: HTMLElement, text: () => string, extra: readonly HTMLElement[] = []): HTMLLabelElement {
    const label = document.createElement('label');
    const shown = document.createElement('span');

    label.className = 'form-field';
    shown.className = 'form-field__caption';
    caption(() => {
      shown.textContent = text();
    });
    label.append(shown, ...extra, control, createFieldMessageSlot());

    return label;
  }

  function input(name: string, type: string): HTMLInputElement {
    const node = document.createElement('input');

    node.name = name;
    node.type = type;

    return node;
  }

  function checkbox(name: string, text: () => string): { label: HTMLLabelElement; toggle: HTMLInputElement } {
    const toggle = input(name, 'checkbox');
    const label = labelled(toggle, text);

    label.classList.add('form-field--check');

    return { label, toggle };
  }

  /** Выбор из порождённого перечня: значения — контракта, подписи — строителем. */
  function enumSelect(name: string, values: readonly string[], keyOf: (value: string) => keyof Strings): HTMLSelectElement {
    const select = document.createElement('select');

    select.name = name;
    select.append(document.createElement('option'));

    for (const value of values) {
      const choice = document.createElement('option');

      choice.value = value;
      caption(() => {
        choice.textContent = enumLabel(strings, keyOf(value));
      });
      select.append(choice);
    }

    return select;
  }

  // ── Город: подсказка справочника по строке поиска ───────────────────────

  interface CityField {
    readonly select: HTMLSelectElement;
    readonly search: HTMLInputElement;
    readonly label: HTMLLabelElement;
    /** Выбранный город — остаётся вариантом и тогда, когда новый поиск его не вернул. */
    chosen: City | null;
    shown: readonly City[];
    /** Поколение поиска: ответ на устаревшую строку к выбору не применяется. */
    generation: number;
  }

  function cityField(name: string, text: () => string): CityField {
    const select = document.createElement('select');
    // Строка поиска имени не несёт: в тело она не уходит, и сообщению сервера
    // ложиться под неё незачем — его поле назовёт выбор города.
    const search = document.createElement('input');

    select.name = name;
    search.type = 'search';
    caption(() => {
      search.placeholder = strings.cargo_listing_form_city_search;
    });

    const field: CityField = {
      select,
      search,
      label: labelled(select, text, [search]),
      chosen: null,
      shown: [],
      generation: 0,
    };

    search.addEventListener('input', () => {
      void suggest(field);
    });
    select.addEventListener('change', () => {
      field.chosen = cityIn(field.shown, select.value);
    });
    fillCities(field, []);

    return field;
  }

  function cityIn(cities: readonly City[], id: string): City | null {
    for (const city of cities) {
      if (String(city.id) === id) {
        return city;
      }
    }

    return null;
  }

  /** Варианты — ответ справочника в его порядке; выбранный город не теряется. */
  function fillCities(field: CityField, found: readonly City[]): void {
    const cities: City[] = [];
    const chosen = field.chosen;

    if (chosen !== null && cityIn(found, String(chosen.id)) === null) {
      cities.push(chosen);
    }

    cities.push(...found);
    field.shown = cities;

    const choices: HTMLOptionElement[] = [document.createElement('option')];

    for (const city of cities) {
      const choice = document.createElement('option');

      choice.value = String(city.id);
      choice.textContent = cityCaption(city);
      choices.push(choice);
    }

    field.select.replaceChildren(...choices);
    field.select.value = chosen === null ? '' : String(chosen.id);
  }

  async function suggest(field: CityField): Promise<void> {
    const query = field.search.value;

    if (query.trim() === '') {
      return;
    }

    field.generation += 1;

    const mine = field.generation;
    const slot = field.label.querySelector(`.${FIELD_MESSAGE_CLASS}`);

    try {
      const found = await client.listCities({ q: query });

      if (mine !== field.generation) {
        return;
      }

      if (slot !== null) {
        slot.textContent = '';
      }

      fillCities(field, found);
    } catch (error) {
      // Годность строки поиска судит сервер; его отказ — текстом под полем,
      // прежние варианты остаются.
      if (mine === field.generation && slot !== null) {
        slot.textContent = refusalText(error, strings);
      }
    }
  }

  // ── Поля формы ──────────────────────────────────────────────────────────

  const origin = cityField('origin_city_id', () => strings.cargo_listing_form_origin);
  const destination = cityField('destination_city_id', () => strings.cargo_listing_form_destination);
  const loadingAt = input('loading_at', 'date');
  const unloadBy = input('unload_by', 'date');
  const vehiclesCount = input('vehicles_count', 'number');
  const cargoType = input('cargo_type', 'text');
  const weight = input('weight_kg', 'number');
  const adr = checkbox('adr', () => strings.cargo_listing_form_adr);
  const bodyType = document.createElement('select');
  const priceAmount = input('price_amount', 'text');
  const currency = enumSelect('price_currency', CURRENCIES, currencyStringKey);
  const negotiable = checkbox('negotiable', () => strings.cargo_listing_form_negotiable);
  const paymentMethod = enumSelect('payment_method', PAYMENT_METHODS, paymentMethodStringKey);
  const paymentMoment = enumSelect('payment_moment', PAYMENT_MOMENTS, paymentMomentStringKey);
  const deferralDays = input('payment_deferral_days', 'number');
  const comment = document.createElement('textarea');

  priceAmount.inputMode = 'decimal';
  bodyType.name = bodyFieldName('type');
  comment.name = 'comment';
  // Новая форма стоит в «торг разрешён» (требование 318); правка — значением
  // объявления.
  negotiable.toggle.checked = true;

  caption(() => {
    submitButton.textContent = listingId === undefined
      ? strings.cargo_listing_form_submit
      : strings.cargo_listing_form_save;
    heading.textContent = listingId === undefined
      ? strings.cargo_listing_form_title_new
      : strings.cargo_listing_form_title_edit;
  });

  form.append(
    origin.label,
    destination.label,
    labelled(loadingAt, () => strings.cargo_listing_form_loading_at),
    labelled(unloadBy, () => strings.cargo_listing_form_unload_by),
    labelled(vehiclesCount, () => strings.cargo_listing_form_vehicles_count),
    labelled(cargoType, () => strings.cargo_listing_form_cargo_type),
    labelled(weight, () => strings.cargo_listing_form_weight),
    adr.label,
    labelled(bodyType, () => strings.cargo_listing_form_body_type),
    bodyArea,
    labelled(priceAmount, () => strings.cargo_listing_form_price),
    labelled(currency, () => strings.cargo_listing_form_currency),
    negotiable.label,
    labelled(paymentMethod, () => strings.cargo_listing_form_payment_method),
    labelled(paymentMoment, () => strings.cargo_listing_form_payment_moment),
    labelled(deferralDays, () => strings.cargo_listing_form_deferral_days),
    labelled(comment, () => strings.cargo_listing_form_comment),
    submitButton,
  );

  // ── Кузов: справочник сервера ───────────────────────────────────────────

  let bodyTypes: readonly BodyTypeInfo[] = [];
  let subtypeSelect: HTMLSelectElement | null = null;
  /** Поля параметров, показанные сейчас, — только они уходят значением. */
  const parameterFields = new Map<BodyFieldName, HTMLInputElement | HTMLSelectElement>();
  /** Введённое в параметры — переживает смену типа у полей, применимых к обоим. */
  const parameterValues = new Map<BodyFieldName, string>();
  /** Подписи полей кузова: пересобираются с полями при смене типа. */
  let bodyCaptions: Array<() => void> = [];

  function chosenBodyType(): BodyTypeInfo | null {
    for (const info of bodyTypes) {
      if (info.type === bodyType.value) {
        return info;
      }
    }

    return null;
  }

  function fillBodyTypes(): void {
    const choices: HTMLOptionElement[] = [document.createElement('option')];

    // Порядок и имена — ответа справочника, на языке ответа (поправка
    // CONTRACT `К4`).
    for (const { type, name, typical } of bodyTypes) {
      const choice = document.createElement('option');

      choice.value = type;
      choice.textContent = `${name} (${typical})`;
      choices.push(choice);
    }

    bodyType.replaceChildren(...choices);
  }

  function bodyLabel(control: HTMLElement, text: () => string): HTMLLabelElement {
    const label = document.createElement('label');
    const shown = document.createElement('span');
    const apply = (): void => {
      shown.textContent = text();
    };

    label.className = 'form-field';
    shown.className = 'form-field__caption';
    bodyCaptions.push(apply);
    apply();
    label.append(shown, control, createFieldMessageSlot());

    return label;
  }

  function parameterControl(name: BodyFieldName): HTMLInputElement | HTMLSelectElement {
    const fieldName = bodyFieldName(name);

    switch (BODY_FIELD_KINDS[name]) {
      case 'loading': {
        const select = document.createElement('select');

        select.name = fieldName;
        select.append(document.createElement('option'));

        for (const value of LOADING_TYPES) {
          const choice = document.createElement('option');

          choice.value = value;
          choice.textContent = enumLabel(strings, loadingTypeStringKey(value));
          bodyCaptions.push(() => {
            choice.textContent = enumLabel(strings, loadingTypeStringKey(value));
          });
          select.append(choice);
        }

        return select;
      }

      case 'integer':
        return input(fieldName, 'number');

      case 'decimal': {
        const node = input(fieldName, 'text');

        node.inputMode = 'decimal';

        return node;
      }
    }
  }

  /**
   * Поля кузова выбранного типа — **ровно** параметры справочника, в его
   * порядке. Поле прежнего типа, неприменимое к новому, исчезает, и его
   * значение не уходит; подтип принадлежит типу и при смене типа снимается.
   */
  function rebuildBody(): void {
    const info = chosenBodyType();
    const parts: HTMLElement[] = [];

    parameterFields.clear();
    bodyCaptions = [];
    subtypeSelect = null;

    if (info !== null && info.subtypes.length > 0) {
      const select = document.createElement('select');

      select.name = bodyFieldName('subtype');
      select.append(document.createElement('option'));

      for (const subtype of info.subtypes) {
        const choice = document.createElement('option');

        choice.value = subtype.value;
        choice.textContent = subtype.name;
        select.append(choice);
      }

      subtypeSelect = select;
      parts.push(bodyLabel(select, () => strings.cargo_listing_form_body_subtype));
    }

    for (const parameter of info === null ? [] : info.parameters) {
      const names = fieldsOf(parameter);

      names.forEach((name, index) => {
        const control = parameterControl(name);

        control.value = parameterValues.get(name) ?? '';
        control.addEventListener('input', () => {
          parameterValues.set(name, control.value);
        });
        control.addEventListener('change', () => {
          parameterValues.set(name, control.value);
        });
        parameterFields.set(name, control);
        // Параметр-пара подписан концами: «от» и «до» по порядку полей схемы.
        parts.push(
          bodyLabel(control, () => {
            const shown = enumLabel(strings, bodyParameterStringKey(parameter));

            if (names.length < 2) {
              return shown;
            }

            const ends = [strings.cargo_listing_form_temperature_from, strings.cargo_listing_form_temperature_to];

            return `${shown} ${ends[index] ?? ''}`.trim();
          }),
        );
      });
    }

    bodyArea.replaceChildren(...parts);
  }

  bodyType.addEventListener('change', () => {
    rebuildBody();
  });

  /** Значение поля кузова формой схемы; непоказанное поле уходит `null`. */
  function bodyValue(name: BodyFieldName): string | number | null {
    const control = parameterFields.get(name);

    if (control === undefined) {
      return null;
    }

    switch (BODY_FIELD_KINDS[name]) {
      case 'decimal':
        return decimalOf(control.value);
      case 'integer':
        return integerOf(control.value);
      case 'loading':
        return pick(LOADING_TYPES, control.value);
    }
  }

  function chosenSubtype(): CargoBodySubmission['subtype'] {
    const info = chosenBodyType();

    if (info === null || subtypeSelect === null) {
      return null;
    }

    for (const subtype of info.subtypes) {
      if (subtype.value === subtypeSelect.value) {
        return subtype.value;
      }
    }

    return null;
  }

  function enteredBody(): CargoBodySubmission {
    const parameters: Partial<Record<BodyFieldName, string | number | null>> = {};

    for (const name of BODY_FIELD_NAMES) {
      parameters[name] = bodyValue(name);
    }

    // Приведение названо: каждое поле записи `BODY_FIELD_KINDS` получило
    // значение своей формы (десятичная строка, целое, тип загрузки), а полноту
    // записи держит её тип — язык типов не выводит этого из обхода ключей.
    return {
      type: pick(BODY_TYPES, bodyType.value),
      subtype: chosenSubtype(),
      ...parameters,
    } as CargoBodySubmission;
  }

  /** Тело — ровно поля формы контракта; пустое поле уходит `null`. */
  function entered(): CargoListingSubmission {
    return {
      origin_city_id: integerOf(origin.select.value),
      destination_city_id: integerOf(destination.select.value),
      loading_at: textOf(loadingAt.value),
      unload_by: textOf(unloadBy.value),
      vehicles_count: integerOf(vehiclesCount.value),
      cargo_type: textOf(cargoType.value),
      weight_kg: integerOf(weight.value),
      adr: adr.toggle.checked,
      body: enteredBody(),
      price_amount: decimalOf(priceAmount.value),
      price_currency: pick(CURRENCIES, currency.value),
      negotiable: negotiable.toggle.checked,
      payment_method: pick(PAYMENT_METHODS, paymentMethod.value),
      payment_moment: pick(PAYMENT_MOMENTS, paymentMoment.value),
      payment_deferral_days: integerOf(deferralDays.value),
      comment: textOf(comment.value),
    };
  }

  /** Правка: форма заполняется значениями объявления из ответа сервера. */
  function fillFrom(listing: CargoListing): void {
    origin.chosen = listing.origin_city;
    fillCities(origin, []);
    destination.chosen = listing.destination_city;
    fillCities(destination, []);
    loadingAt.value = listing.loading_at;
    unloadBy.value = listing.unload_by;
    vehiclesCount.value = String(listing.vehicles_count);
    cargoType.value = listing.cargo_type;
    weight.value = String(listing.weight_kg);
    adr.toggle.checked = listing.adr;
    selectEnumOption(bodyType, bodyTypeStringKey(listing.body.type), bodyTypeStringKey);

    for (const name of BODY_FIELD_NAMES) {
      const value = listing.body[name];

      if (value !== null) {
        parameterValues.set(name, String(value));
      }
    }

    rebuildBody();

    if (subtypeSelect !== null) {
      subtypeSelect.value = listing.body.subtype ?? '';
    }

    priceAmount.value = listing.price === null ? '' : listing.price.amount;
    currency.value = listing.price === null ? '' : listing.price.currency;
    negotiable.toggle.checked = listing.negotiable;
    paymentMethod.value = listing.payment.method;
    paymentMoment.value = listing.payment.moment;
    deferralDays.value = listing.payment.deferral_days === null ? '' : String(listing.payment.deferral_days);
    comment.value = listing.comment ?? '';
  }

  // ── Состояние экрана и отправка ─────────────────────────────────────────

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — справочник кузовов (и объявление правки). */
  let answered = false;
  /** Запрос в пути: повтор отправки до ответа не уходит вторым запросом. */
  let sending = false;
  /** Ключ идемпотентности текущей отправки создания либо `null`. */
  let idempotencyKey: string | null = null;

  function render(): void {
    // Правило места состояния — одна функция на все экраны (долг `TD-66`):
    // справочник не пришёл — формы без него нет, и состояние встаёт на место
    // экрана; пришёл — форма на месте, общий отказ ложится над ней.
    placeState(element, { state, answered, frame: [heading, notice], list: form }, strings);
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    render();

    try {
      if (listingId === undefined) {
        bodyTypes = await client.listBodyTypes();
        fillBodyTypes();
        rebuildBody();
      } else {
        const [types, listing] = await Promise.all([client.listBodyTypes(), client.getCargoListing(listingId)]);

        bodyTypes = types;
        fillBodyTypes();
        fillFrom(listing);
      }

      answered = true;
      state = screenStateFor({ kind: 'done', empty: false });
    } catch (error) {
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function send(): Promise<void> {
    sending = true;
    submitButton.disabled = true;
    notice.textContent = '';
    renderFieldMessages(form, []);

    const body = entered();

    try {
      let saved: CargoListing;

      if (listingId === undefined) {
        idempotencyKey ??= newIdempotencyKey();
        saved = await client.createCargoListing(companyId, body, idempotencyKey);
      } else {
        saved = await client.updateCargoListing(listingId, body);
      }

      idempotencyKey = null;
      // Созданное и исправленное открываются карточкой: километраж, транзит и
      // доску она перечитывает у сервера, а не дописывает клиентом.
      navigate({ kind: 'cargoListing', companyId, listingId: saved.id });
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;

      if (failure !== null && failure.code !== null) {
        // Сервер ответил — следующая отправка новая и несёт новый ключ.
        idempotencyKey = null;
      }

      // Экран остаётся формой со всем введённым: сообщения сервера — под
      // полями, которые он назвал; непоказанное и общий отказ — над формой.
      const homeless = renderFieldMessages(form, failure?.details ?? []);
      const texts: string[] = [];

      for (const detail of homeless) {
        texts.push(detail.message);
      }

      if (texts.length === 0) {
        texts.push(refusalText(error, strings));
      }

      notice.textContent = texts.join(' ');
    } finally {
      sending = false;
      submitButton.disabled = false;
    }
  }

  // Правка любого поля — новая отправка: ключ прежней снимается (решение `Б5`).
  form.addEventListener('input', () => {
    idempotencyKey = null;
  });
  form.addEventListener('change', () => {
    idempotencyKey = null;
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (sending) {
      return;
    }

    void send();
  });

  function relabel(next: Strings): void {
    strings = next;

    for (const apply of captions) {
      apply();
    }

    for (const apply of bodyCaptions) {
      apply();
    }

    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
    relabel,
  };
}
